class OnaScrollRevealText extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._threshold = 0.5;
    this._lines = [];
    this._resizeObserver = null;
    this._intersectionObserver = null;
    this._debounceTimer = null;
    this._originalText = "";
    this._originalParagraph = null;
    this._setupStyles();
  }
  static get observedAttributes() {
    return ["threshold"];
  }
  get threshold() {
    return this._threshold;
  }
  set threshold(value) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
      this._threshold = numValue;
      if (this._lines.length > 0) {
        this._observeLines();
      }
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "threshold" && oldValue !== newValue) {
      this.threshold = newValue;
    }
  }
  connectedCallback() {
    const thresholdAttr = this.getAttribute("threshold");
    if (thresholdAttr) {
      this.threshold = thresholdAttr;
    }
    this._setupObservers();
    this._extractOriginalText();
    this._windowResizeHandler = () => {
      if (this._lines.length > 0) {
        this._observeLines();
      }
    };
    window.addEventListener("resize", this._windowResizeHandler);
  }
  disconnectedCallback() {
    this._cleanupObservers();
    if (this._windowResizeHandler) {
      window.removeEventListener("resize", this._windowResizeHandler);
    }
  }
  _setupStyles() {
    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: 100%;
      }
      
      ::slotted(p) {
        display: none;
      }
      
      .line {
        display: block;
      }
    `;
    this.shadowRoot.appendChild(style);
  }
  _extractOriginalText() {
    const pElement = this.querySelector("p");
    if (pElement) {
      this._originalText = pElement.textContent || "";
      this._originalParagraph = pElement;
      pElement.style.visibility = "hidden";
      this._processText();
    }
  }
  _setupObservers() {
    this._resizeObserver = new ResizeObserver(() => {
      this._debounceRecompute();
    });
    this._resizeObserver.observe(this);
  }
  _cleanupObservers() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }
  _debounceRecompute() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
    this._debounceTimer = setTimeout(() => {
      this._processText();
    }, 150);
  }
  _processText() {
    if (!this._originalText) return;
    const tempElement = document.createElement("div");
    const computedStyle = getComputedStyle(this);
    tempElement.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: ${this.offsetWidth}px;
      font-family: ${computedStyle.fontFamily};
      font-size: ${computedStyle.fontSize};
      font-weight: ${computedStyle.fontWeight};
      line-height: ${computedStyle.lineHeight};
      letter-spacing: ${computedStyle.letterSpacing};
      word-spacing: ${computedStyle.wordSpacing};
      white-space: normal;
      overflow-wrap: break-word;
      padding: ${computedStyle.paddingTop} ${computedStyle.paddingRight} ${computedStyle.paddingBottom} ${computedStyle.paddingLeft};
      margin: ${computedStyle.marginTop} ${computedStyle.marginRight} ${computedStyle.marginBottom} ${computedStyle.marginLeft};
    `;
    document.body.appendChild(tempElement);
    tempElement.textContent = "M";
    const singleLineHeight = tempElement.offsetHeight;
    const words = this._originalText.split(/\s+/);
    const lines = [];
    let currentLine = "";
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      tempElement.textContent = testLine;
      const testHeight = tempElement.offsetHeight;
      if (testHeight > singleLineHeight && currentLine) {
        lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }
    document.body.removeChild(tempElement);
    this._lines = lines.map((text, index) => ({
      id: `line-${index}`,
      text,
      active: false,
      element: null
    }));
    this._renderLines();
    this._observeLines();
  }
  _renderLines() {
    const style = this.shadowRoot.querySelector("style");
    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this._lines.forEach((line, index) => {
      const span = document.createElement("span");
      span.className = "line";
      span.textContent = line.text;
      span.setAttribute("part", "line");
      span.setAttribute("data-text", line.text);
      span.dataset.lineId = line.id;
      this.shadowRoot.appendChild(span);
      this._lines[index].element = span;
    });
    const slot = document.createElement("slot");
    this.shadowRoot.appendChild(slot);
  }
  _observeLines() {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
    const viewportHeight = window.innerHeight;
    const triggerPoint = viewportHeight * this._threshold;
    const rootMarginTop = -(viewportHeight - triggerPoint);
    const rootMarginBottom = -(viewportHeight + rootMarginTop);
    this._intersectionObserver = new IntersectionObserver((entries) => this._handleIntersection(entries), {
      threshold: 0,
      rootMargin: `0px 0px ${rootMarginTop}px 0px`
    });
    this._lines.forEach((line) => {
      if (line.element) {
        this._intersectionObserver.observe(line.element);
      }
    });
  }
  _handleIntersection(entries) {
    entries.forEach((entry) => {
      const lineId = entry.target.dataset.lineId;
      const line = this._lines.find((l) => l.id === lineId);
      if (line) {
        const shouldBeActive = entry.isIntersecting;
        if (line.active !== shouldBeActive) {
          line.active = shouldBeActive;
          const partValue = line.active ? "line active" : "line";
          line.element.setAttribute("part", partValue);
        }
      }
    });
  }
}
customElements.define("ona-scroll-reveal-text", OnaScrollRevealText);
